import { useMemo, useState, Fragment } from "react";
import {
  Search, Check, X, DollarSign, Car, Phone, MapPin, Gauge, Mail, Plus, Pencil,
  Trash2, ShieldAlert, Clock, CheckCircle2, UserCheck, Plane, Star, ChevronDown, ExternalLink, Eye, Ban, Landmark,
} from "lucide-react";
import { money } from "@/lib/format";
import { HostDetailModal } from "./HostDetailModal";

const TODAY = new Date().toISOString().slice(0, 10);
const STATUS = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700",
};
const CLAIM_TYPES = ["Damage", "Cleaning", "Smoking / odor", "Late return", "Mileage overage", "Tolls / Tickets", "Other"];
const CLAIM_STATUS_COLORS = { open: "bg-amber-100 text-amber-700", resolved: "bg-green-100 text-green-700", waived: "bg-slate-100 text-slate-500" };
const PAY_LABELS = { cash: "Cash", zelle: "Zelle", cashapp: "Cash App", venmo: "Venmo" };

function SectionEmpty({ children }) {
  return <p className="text-slate-400 p-8 text-sm">{children}</p>;
}

// ---------------- TRIPS ----------------
export function TripsPanel({ bookings, updateStatus, resendReceipt, onClaim, onRecordPayment, onMutate }) {
  const [sub, setSub] = useState("upcoming");
  const [q, setQ] = useState("");

  const cancelledCount = useMemo(() => bookings.filter((b) => b.status === "cancelled").length, [bookings]);
  const paidCount = useMemo(() => bookings.filter((b) => b.payment_status === "paid").length, [bookings]);

  const buckets = useMemo(() => {
    const up = [], act = [], hist = [];
    for (const b of bookings) {
      const ended = (b.return_date || "") < TODAY;
      const done = b.status === "completed" || b.status === "cancelled";
      const started = (b.pickup_date || "") <= TODAY;
      if (done || ended) hist.push(b);
      else if (started && !ended) act.push(b);
      else up.push(b);
    }
    return { upcoming: up, active: act, history: hist };
  }, [bookings]);

  const list = (buckets[sub] || []).filter((b) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [b.customer_name, b.customer_email, b.car_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  const SUBS = [["upcoming", "Upcoming", buckets.upcoming.length], ["active", "Active", buckets.active.length], ["history", "History", buckets.history.length]];

  return (
    <div data-testid="trips-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="trips-stats">
        <div className="bg-white rounded-2xl shadow-card p-4"><p className="text-2xl font-bold font-heading text-slate-900">{bookings.length}</p><p className="text-xs text-slate-500 mt-0.5">Total reservations</p></div>
        <div className="bg-white rounded-2xl shadow-card p-4"><p className="text-2xl font-bold font-heading text-emerald-600">{paidCount}</p><p className="text-xs text-slate-500 mt-0.5">Paid</p></div>
        <div className="bg-white rounded-2xl shadow-card p-4"><p className="text-2xl font-bold font-heading text-blue-600">{buckets.active.length + buckets.upcoming.length}</p><p className="text-xs text-slate-500 mt-0.5">Active + upcoming</p></div>
        <div className="bg-white rounded-2xl shadow-card p-4"><p className="text-2xl font-bold font-heading text-rose-500" data-testid="trips-cancelled-count">{cancelledCount}</p><p className="text-xs text-slate-500 mt-0.5">Cancelled</p></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2 bg-white rounded-full p-1.5 shadow-card">
          {SUBS.map(([k, label, n]) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`trips-sub-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${sub === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              {label} <span className={`ml-1 ${sub === k ? "text-white/70" : "text-slate-400"}`}>{n}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="trips-search" placeholder="Search trips…" className="belo-input pl-9 w-64" />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {list.length === 0 ? <SectionEmpty>No {sub} trips.</SectionEmpty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Customer</th><th className="py-4 px-3">Vehicle</th><th className="py-4 px-3">Dates</th><th className="py-4 px-3">Total</th><th className="py-4 px-3">Status</th><th className="py-4 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id} data-testid={`trip-row-${b.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-3.5 px-5"><div className="text-slate-900 font-medium">{b.customer_name}</div><div className="text-slate-400 text-xs">{b.customer_email}</div></td>
                    <td className="py-3.5 px-3 text-slate-700">{b.car_name}{b.is_host_car && <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700"><UserCheck size={11} /> Host</span>}</td>
                    <td className="py-3.5 px-3 text-slate-500">{b.pickup_date} → {b.return_date} <span className="text-slate-400">({b.days}d)</span>{b.airport_dropoff && <span className="ml-1 inline-flex items-center gap-1 text-blue-700 text-xs"><Plane size={11} /></span>}</td>
                    <td className="py-3.5 px-3 text-slate-900 font-medium">{money(b.amount_due)}{b.payment_status === "paid" ? <div className="text-green-600 text-xs">Paid{b.payment_method ? ` · ${PAY_LABELS[b.payment_method] || b.payment_method}` : ""}</div> : <div className="text-slate-400 text-xs">Unpaid</div>}{b.source === "admin" && <div className="text-purple-600 text-[10px] font-semibold uppercase tracking-wide mt-0.5">Manual</div>}</td>
                    <td className="py-3.5 px-3">
                      <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} data-testid={`trip-status-${b.id}`} className={`px-3 py-1.5 text-xs font-semibold capitalize rounded-full border-0 outline-none cursor-pointer ${STATUS_COLORS[b.status] || ""}`}>
                        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-2 justify-end">
                        {b.source === "admin" && b.payment_status !== "paid" && onRecordPayment && (
                          <select defaultValue="" onChange={(e) => { if (e.target.value) { onRecordPayment(b.id, e.target.value); e.target.value = ""; } }} data-testid={`trip-record-payment-${b.id}`} title="Record offline payment" className="text-xs font-semibold rounded-full border border-slate-200 px-2 py-1.5 text-emerald-700 cursor-pointer outline-none">
                            <option value="">Record payment…</option>
                            <option value="cash">Cash</option>
                            <option value="zelle">Zelle</option>
                            <option value="cashapp">Cash App</option>
                            <option value="venmo">Venmo</option>
                          </select>
                        )}
                        <button onClick={() => onClaim(b)} data-testid={`trip-claim-${b.id}`} title="Open a claim" className="text-slate-400 hover:text-rose-600 transition-colors"><ShieldAlert size={16} /></button>
                        <button onClick={() => resendReceipt(b.id)} data-testid={`trip-receipt-${b.id}`} title="Resend receipt" className="text-slate-400 hover:text-blue-600 transition-colors"><Mail size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- HOSTS ----------------
const VSTATUS = { approved: "Live", under_review: "Under review", rejected: "Rejected" };

function HostsOverview({ data, onViewHost }) {
  const hosts = data?.hosts || [];
  const totals = data?.totals || {};
  const KPIS = [
    ["Hosts", totals.hosts ?? 0],
    ["Vehicles", totals.vehicles ?? 0],
    ["Gross revenue", money(totals.gross ?? 0)],
    ["Service fee revenue", money(totals.commission ?? 0)],
    ["Ready to pay", money(totals.payable ?? 0)],
  ];
  return (
    <div data-testid="hosts-overview">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {KPIS.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl shadow-card p-5">
            <p className="label-caps text-slate-400 text-[10px]">{l}</p>
            <p className="font-heading text-2xl mt-2 font-bold">{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {hosts.length === 0 ? <SectionEmpty>No hosts yet. Once someone lists a vehicle they'll appear here.</SectionEmpty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Host</th><th className="py-4 px-3">Vehicles</th><th className="py-4 px-3">Trips</th>
                <th className="py-4 px-3">Gross revenue</th><th className="py-4 px-3">Host earnings</th><th className="py-4 px-3">Service fee</th>
                <th className="py-4 px-3">Rating</th><th className="py-4 px-3 text-right"></th>
              </tr></thead>
              <tbody>
                {hosts.map((h) => (
                  <tr key={h.id} data-testid={`host-row-${h.id}`} onClick={() => onViewHost(h.id)} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer">
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{h.business_name || h.name}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${h.host_type === "business" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>{h.host_type === "business" ? "Business" : "Individual"}</span>
                      </div>
                      <div className="text-slate-400 text-xs">{h.email}</div>
                    </td>
                    <td className="py-3.5 px-3 text-slate-700">{h.vehicles_count.approved}/{h.vehicles_count.total}{h.vehicles_count.under_review > 0 && <span className="text-amber-600 text-xs ml-1">({h.vehicles_count.under_review} in review)</span>}</td>
                    <td className="py-3.5 px-3 text-slate-600">{h.trips}</td>
                    <td className="py-3.5 px-3 text-slate-900 font-semibold">{money(h.gross_revenue)}</td>
                    <td className="py-3.5 px-3 text-slate-600">{money(h.host_earnings)}</td>
                    <td className="py-3.5 px-3 text-green-600 font-medium">{money(h.platform_commission)}</td>
                    <td className="py-3.5 px-3">{h.rating.count ? <span className="inline-flex items-center gap-1 text-slate-700"><Star size={13} className="fill-amber-400 text-amber-400" />{h.rating.avg} <span className="text-slate-400">({h.rating.count})</span></span> : <span className="text-slate-300">—</span>}</td>
                    <td className="py-3.5 px-3 text-right"><span data-testid={`host-view-${h.id}`} className="text-blue-600 font-semibold text-xs inline-flex items-center gap-1">View <ChevronDown size={14} className="-rotate-90" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function HostsPanel({ applications, cars, hosts, onApprove, onReject }) {
  const [view, setView] = useState("hosts");
  const [detailHost, setDetailHost] = useState(null);
  const hostList = hosts?.hosts || [];
  const openHost = (id) => { const h = hostList.find((x) => x.id === id); if (h) setDetailHost(h); };
  const pendingCount = hostList.reduce((s, h) => s + (h.vehicles_count?.under_review || 0), 0);
  const SUBS = [["hosts", "Hosts", hosts?.totals?.hosts ?? hostList.length], ["listings", "Listings", pendingCount]];

  return (
    <div data-testid="hosts-panel">
      <div className="flex gap-2 bg-white rounded-full p-1.5 shadow-card mb-5 w-fit">
        {SUBS.map(([k, label, n]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`hosts-view-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            {label} <span className={`ml-1 ${view === k ? "text-white/70" : "text-slate-400"}`}>{n}</span>
          </button>
        ))}
      </div>
      {view === "hosts"
        ? <HostsOverview data={hosts} onViewHost={openHost} />
        : <ListingsReview applications={applications} cars={cars} onApprove={onApprove} onReject={onReject} onViewHost={openHost} />}
      {detailHost && <HostDetailModal host={detailHost} onClose={() => setDetailHost(null)} />}
    </div>
  );
}

function ListingsReview({ applications, cars, onApprove, onReject, onViewHost }) {
  const [filter, setFilter] = useState("under_review");
  const carMap = useMemo(() => Object.fromEntries(cars.map((c) => [c.id, c])), [cars]);

  const rows = applications.filter((a) => carMap[a.car_id]).map((a) => {
    const car = carMap[a.car_id];
    return { ...a, review_status: car.review_status || a.status, available: car.available, car };
  });
  const counts = {
    under_review: rows.filter((r) => r.review_status === "under_review").length,
    approved: rows.filter((r) => r.review_status === "approved").length,
    rejected: rows.filter((r) => r.review_status === "rejected").length,
  };
  const filtered = filter === "all" ? rows : rows.filter((r) => r.review_status === filter);
  const FILTERS = [["under_review", "Pending review", counts.under_review], ["approved", "Approved", counts.approved], ["rejected", "Rejected", counts.rejected], ["all", "All", rows.length]];

  return (
    <div data-testid="listings-review">
      <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-3 mb-5 text-sm text-blue-800">
        Approve a listing to publish it on the public fleet. On a booked host trip, Belo collects payment and pays the host <b>100%</b> of the trip price (rental + delivery + young-driver fee) after the trip is returned — Belo's revenue is a separate <b>10%</b> service fee charged to the guest, plus all sales tax.
      </div>
      <div className="flex gap-2 bg-white rounded-full p-1.5 shadow-card mb-5 w-fit">
        {FILTERS.map(([k, label, n]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`hosts-filter-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            {label} <span className={`ml-1 ${filter === k ? "text-white/70" : "text-slate-400"}`}>{n}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card"><SectionEmpty>No host listings here.</SectionEmpty></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map((r) => {
            const pending = r.review_status === "under_review";
            const rejected = r.review_status === "rejected";
            return (
              <div key={r.id} data-testid={`host-app-${r.car_id}`} className={`bg-white rounded-2xl shadow-card p-5 ${pending ? "ring-2 ring-amber-200" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="label-caps text-blue-600">{r.year} {r.make}</p>
                    <h3 className="font-heading text-lg font-semibold">{r.year} {r.make} {r.model} {r.trim}</h3>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${pending ? "bg-amber-100 text-amber-700" : rejected ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                    {pending ? "Under Review" : rejected ? "Rejected" : "Approved & Live"}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs text-slate-600">
                  <p className="flex items-center gap-1.5"><UserCheck size={12} /> {r.full_name}</p>
                  <p className="flex items-center gap-1.5"><Phone size={12} /> {r.phone}</p>
                  <p className="flex items-center gap-1.5"><Mail size={12} /> {r.email}</p>
                  <p className="flex items-center gap-1.5"><Gauge size={12} /> {Number(r.odometer || 0).toLocaleString()} mi</p>
                  {r.vin && <p className="font-mono col-span-2">VIN {r.vin}</p>}
                  {r.address && <p className="flex items-center gap-1.5 col-span-2"><MapPin size={12} /> {r.address}</p>}
                </div>
                {rejected && r.reason && <p className="mt-3 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">Reason: {r.reason}</p>}
                {onViewHost && r.car?.host_id && (
                  <button onClick={() => onViewHost(r.car.host_id)} data-testid={`listing-view-host-${r.car_id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
                    <UserCheck size={14} /> View host profile <ExternalLink size={13} />
                  </button>
                )}
                {pending && (
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => onApprove(r.car_id)} data-testid={`host-approve-${r.car_id}`} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"><Check size={15} /> Approve & Publish</button>
                    <button onClick={() => onReject(r)} data-testid={`host-reject-${r.car_id}`} className="flex-1 flex items-center justify-center gap-2 border-2 border-red-200 text-red-600 py-2 rounded-full text-sm font-semibold hover:bg-red-50 transition-colors"><X size={15} /> Reject</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------- PAYOUTS ----------------
function StateBadge({ state }) {
  const map = { payable: ["bg-green-100 text-green-700", CheckCircle2, "Ready to pay"], pending: ["bg-amber-100 text-amber-700", Clock, "Pending trip"], paid: ["bg-slate-100 text-slate-500", Check, "Paid"] };
  const [cls, Icon, label] = map[state] || map.pending;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}><Icon size={12} /> {label}</span>;
}

export function PayoutsPanel({ payouts, onMarkPaid, onStripeTransfer }) {
  const { hosts = [], bookings = [] } = payouts || {};
  const totalPayable = hosts.reduce((s, h) => s + (h.payable || 0), 0);
  const totalPending = hosts.reduce((s, h) => s + (h.pending || 0), 0);
  const totalPaid = hosts.reduce((s, h) => s + (h.paid || 0), 0);

  return (
    <div data-testid="payouts-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[["Ready to pay", totalPayable, "text-green-600"], ["Pending (trip active)", totalPending, "text-amber-600"], ["Paid out", totalPaid, "text-slate-500"]].map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-2xl shadow-card p-5">
            <p className="label-caps text-slate-400">{label}</p>
            <p className={`font-heading text-3xl mt-2 font-bold ${cls}`}>{money(val)}</p>
          </div>
        ))}
      </div>

      {hosts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="payout-hosts">
          {hosts.map((h) => (
            <div key={h.host_name} data-testid={`payout-host-${h.host_id || h.host_name}`} className="bg-white rounded-2xl shadow-card p-5">
              <p className="font-heading font-semibold text-slate-900">{h.host_name}</p>
              <p className="text-slate-400 text-xs">{h.count} trip{h.count !== 1 ? "s" : ""}</p>
              <p className="text-green-600 font-bold text-2xl mt-3">{money(h.payable)} <span className="text-slate-400 text-xs font-medium">ready</span></p>
              <p className="text-slate-400 text-xs mt-1">{money(h.pending)} pending · {money(h.paid)} paid</p>
              {h.bank ? (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid={`payout-bank-${h.host_id || h.host_name}`}>
                  <p className="font-semibold text-slate-600 flex items-center gap-1.5"><Landmark size={12} /> ACH payout</p>
                  <p className="mt-1">{h.bank.account_holder} · {h.bank.account_type}</p>
                  <p className="font-mono">Routing {h.bank.routing || `••••${h.bank.routing_last4 || ""}`} · Acct ••••{h.bank.account_last4}</p>
                </div>
              ) : (
                <p className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">No ACH details on file</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {bookings.length === 0 ? <SectionEmpty>No host bookings yet. Once a host's car is booked and paid, payouts appear here.</SectionEmpty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Host</th><th className="py-4 px-3">Vehicle / Guest</th><th className="py-4 px-3">Returned</th><th className="py-4 px-3">Trip price</th><th className="py-4 px-3">Service fee (10%)</th><th className="py-4 px-3">Payout</th><th className="py-4 px-3">Status</th><th className="py-4 px-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} data-testid={`payout-row-${b.booking_id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-3.5 px-5 text-slate-900 font-medium">{b.host_name}</td>
                    <td className="py-3.5 px-3 text-slate-600">{b.car_name}<div className="text-slate-400 text-xs">{b.customer_name}</div></td>
                    <td className="py-3.5 px-3 text-slate-500">{b.return_date}{b.has_open_claim && <div className="text-rose-600 text-xs">Open claim</div>}</td>
                    <td className="py-3.5 px-3 text-slate-700">{money(b.trip_price)}</td>
                    <td className="py-3.5 px-3 text-emerald-600">{money(b.service_fee)}</td>
                    <td className="py-3.5 px-3 text-slate-900 font-semibold">{money(b.host_payout)}</td>
                    <td className="py-3.5 px-3"><StateBadge state={b.state} /></td>
                    <td className="py-3.5 px-3 text-right">
                      {b.state === "payable" ? (
                        <div className="flex items-center gap-2 justify-end">
                          {onStripeTransfer && <button onClick={() => onStripeTransfer(b.booking_id)} data-testid={`stripe-transfer-${b.booking_id}`} className="border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-emerald-50 transition-colors">Pay via Stripe</button>}
                          <button onClick={() => onMarkPaid(b.booking_id)} data-testid={`mark-paid-${b.booking_id}`} className="bg-blue-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold hover:bg-blue-700 transition-colors">Mark paid</button>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- CLAIMS ----------------
export function ClaimsPanel({ claims, bookings = [], onNew, onEdit, onUpdate, onDelete }) {
  const [filter, setFilter] = useState("open");
  const [detail, setDetail] = useState(null);
  const counts = { open: claims.filter((c) => c.status === "open").length, resolved: claims.filter((c) => c.status === "resolved").length, waived: claims.filter((c) => c.status === "waived").length };
  const list = filter === "all" ? claims : claims.filter((c) => c.status === filter);
  const FILTERS = [["open", "Open", counts.open], ["resolved", "Resolved", counts.resolved], ["waived", "Waived", counts.waived], ["all", "All", claims.length]];

  const sum = useMemo(() => {
    const amt = (arr) => arr.reduce((a, c) => a + (Number(c.amount) || 0), 0);
    const byType = {};
    for (const c of claims) {
      (byType[c.type] ||= { type: c.type, count: 0, amount: 0 });
      byType[c.type].count++; byType[c.type].amount += Number(c.amount) || 0;
    }
    const dep = claims.filter((c) => c.charge_source === "deposit");
    const ext = claims.filter((c) => c.charge_source !== "deposit");
    return {
      claimed: amt(claims), recovered: amt(claims.filter((c) => c.status === "resolved")),
      openAmt: amt(claims.filter((c) => c.status === "open")), waivedAmt: amt(claims.filter((c) => c.status === "waived")),
      byType: Object.values(byType).sort((a, b) => b.amount - a.amount),
      depAmt: amt(dep), depCount: dep.length, extAmt: amt(ext), extCount: ext.length,
    };
  }, [claims]);
  const bmap = useMemo(() => Object.fromEntries((bookings || []).map((b) => [b.id, b])), [bookings]);

  return (
    <div data-testid="claims-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <ClaimKpi icon={Clock} tone="amber" label="Open claims" value={counts.open} sub={`${money(sum.openAmt)} outstanding`} />
        <ClaimKpi icon={CheckCircle2} tone="green" label="Recovered" value={money(sum.recovered)} sub={`${counts.resolved} resolved`} />
        <ClaimKpi icon={Ban} tone="slate" label="Waived" value={counts.waived} sub={`${money(sum.waivedAmt)} written off`} />
        <ClaimKpi icon={DollarSign} tone="blue" label="Total claimed" value={money(sum.claimed)} sub={`${claims.length} claims · avg ${money(claims.length ? sum.claimed / claims.length : 0)}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl shadow-card p-5 lg:col-span-2">
          <p className="label-caps text-slate-400 mb-3">Claimed by type</p>
          {sum.byType.length === 0 ? <p className="text-slate-400 text-sm">No claims yet.</p> : (
            <div className="space-y-2.5">
              {sum.byType.map((t) => {
                const pct = sum.claimed ? Math.round((t.amount / sum.claimed) * 100) : 0;
                return (
                  <div key={t.type} data-testid={`claim-type-${t.type}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="text-slate-700">{t.type} <span className="text-slate-400">({t.count})</span></span><span className="font-semibold text-slate-900">{money(t.amount)}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-card p-5">
          <p className="label-caps text-slate-400 mb-3">Charged to</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Security deposit</span><span className="font-semibold text-slate-900">{money(sum.depAmt)} <span className="text-slate-400 text-xs font-normal">({sum.depCount})</span></span></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Extra charge</span><span className="font-semibold text-slate-900">{money(sum.extAmt)} <span className="text-slate-400 text-xs font-normal">({sum.extCount})</span></span></div>
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between"><span className="text-sm font-semibold text-slate-700">Recovered</span><span className="font-bold text-emerald-600">{money(sum.recovered)}</span></div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2 bg-white rounded-full p-1.5 shadow-card">
          {FILTERS.map(([k, label, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`claims-filter-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              {label} <span className={`ml-1 ${filter === k ? "text-white/70" : "text-slate-400"}`}>{n}</span>
            </button>
          ))}
        </div>
        <button onClick={onNew} data-testid="new-claim-btn" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors"><Plus size={16} /> New Claim</button>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {list.length === 0 ? <SectionEmpty>No claims here. Open a claim from a trip to charge cleaning, damage, smoking, tolls, or mileage overage.</SectionEmpty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Guest / Vehicle</th><th className="py-4 px-3">Type</th><th className="py-4 px-3">Amount</th><th className="py-4 px-3">Charge to</th><th className="py-4 px-3">Status</th><th className="py-4 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} data-testid={`claim-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-3.5 px-5"><div className="text-slate-900 font-medium">{c.customer_name || "—"}</div><div className="text-slate-400 text-xs">{c.car_name}</div></td>
                    <td className="py-3.5 px-3 text-slate-700">{c.type}{c.description && <div className="text-slate-400 text-xs truncate max-w-[220px]">{c.description}</div>}</td>
                    <td className="py-3.5 px-3 text-slate-900 font-semibold">{money(c.amount)}</td>
                    <td className="py-3.5 px-3 text-slate-500 capitalize">{c.charge_source === "deposit" ? "Deposit" : "Extra charge"}</td>
                    <td className="py-3.5 px-3">
                      <select value={c.status} onChange={(e) => onUpdate(c.id, { status: e.target.value })} data-testid={`claim-status-${c.id}`} className={`px-3 py-1.5 text-xs font-semibold capitalize rounded-full border-0 outline-none cursor-pointer ${CLAIM_STATUS_COLORS[c.status] || ""}`}>
                        {["open", "resolved", "waived"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="py-3.5 px-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setDetail(c)} data-testid={`view-claim-${c.id}`} className="text-slate-500 hover:text-blue-600"><Eye size={15} /></button>
                        <button onClick={() => onEdit(c)} data-testid={`edit-claim-${c.id}`} className="text-slate-500 hover:text-slate-900"><Pencil size={15} /></button>
                        <button onClick={() => onDelete(c.id)} data-testid={`delete-claim-${c.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detail && <ClaimDetailModal claim={detail} booking={bmap[detail.booking_id]} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ClaimKpi({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = { blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-600" };
  return (
    <div data-testid={`claim-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white rounded-2xl shadow-card p-5">
      <span className={`grid place-items-center w-10 h-10 rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
      <p className="font-heading text-2xl mt-3 font-bold text-slate-900">{value}</p>
      <p className="label-caps text-slate-400 mt-1 text-[10px]">{label}</p>
      {sub && <p className="text-slate-400 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function ClaimDetailModal({ claim, booking, onClose }) {
  const st = CLAIM_STATUS_COLORS[claim.status] || "";
  const fmtDt = (s) => { const d = new Date(s); return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };
  const rows = [
    ["Guest", claim.customer_name || "—"],
    ["Vehicle", claim.car_name || "—"],
    ["Host", claim.host_name || "Belo fleet"],
    ["Trip dates", booking ? `${booking.pickup_date} → ${booking.return_date}${booking.days ? ` (${booking.days}d)` : ""}` : "—"],
    ["Charge to", claim.charge_source === "deposit" ? "Security deposit" : "Extra charge"],
  ];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="claim-detail-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-xl font-semibold">Claim detail</h2>
            <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full capitalize ${st}`}>{claim.status}</span>
          </div>
          <button onClick={onClose} data-testid="claim-detail-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6">
          <p className="label-caps text-blue-600">{claim.type}</p>
          <p className="font-heading text-3xl font-bold text-slate-900 mt-1">{money(claim.amount)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-6">
            {rows.map(([l, v]) => (<div key={l}><p className="label-caps text-slate-400 text-[10px]">{l}</p><p className="text-slate-800 text-sm mt-0.5">{v}</p></div>))}
          </div>
          {claim.description && <div className="mt-5"><p className="label-caps text-slate-400 text-[10px] mb-1">Description</p><p className="text-slate-700 text-sm bg-slate-50 rounded-xl p-3">{claim.description}</p></div>}
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><Clock size={13} /> Opened {fmtDt(claim.created_at)}</span>
            {claim.resolved_at && <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> Closed {fmtDt(claim.resolved_at)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- MODALS ----------------
export function ClaimModal({ data, bookings, onClose, onSave }) {
  const [form, setForm] = useState({
    booking_id: data.booking_id || "", type: data.type || "Damage", amount: data.amount ?? "",
    charge_source: data.charge_source || "deposit", description: data.description || "",
    status: data.status || "open", id: data.id,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const editing = !!data.id;
  const eligible = bookings.filter((b) => b.payment_status === "paid" || b.status !== "cancelled");

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="claim-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-bold mb-4">{editing ? "Edit claim" : "New claim"}</h3>
        <div className="space-y-3">
          {!editing && (
            <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Trip</span>
              <select value={form.booking_id} onChange={set("booking_id")} data-testid="claim-booking" className="belo-input">
                <option value="">Select a trip…</option>
                {eligible.map((b) => <option key={b.id} value={b.id}>{b.customer_name} · {b.car_name} · {b.pickup_date}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Type</span>
              <select value={form.type} onChange={set("type")} data-testid="claim-type" className="belo-input">{CLAIM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
            </label>
            <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Amount ($)</span>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} data-testid="claim-amount" className="belo-input" placeholder="0.00" /></label>
          </div>
          <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Charge to</span>
            <select value={form.charge_source} onChange={set("charge_source")} data-testid="claim-source" className="belo-input">
              <option value="deposit">Deposit ($300 held)</option><option value="extra">Extra charge (beyond deposit)</option>
            </select>
          </label>
          {editing && (
            <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Status</span>
              <select value={form.status} onChange={set("status")} data-testid="claim-status-select" className="belo-input">{["open", "resolved", "waived"].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
          )}
          <label className="block"><span className="label-caps text-slate-400 block mb-1.5">Description</span>
            <textarea value={form.description} onChange={set("description")} data-testid="claim-description" rows={3} className="belo-input" placeholder="What happened?" /></label>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 border-2 border-slate-200 text-slate-600 py-2.5 rounded-full font-semibold text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => { if (!form.booking_id) return; onSave(form); }} disabled={!form.booking_id} data-testid="save-claim-btn" className="flex-1 bg-blue-600 text-white py-2.5 rounded-full font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{editing ? "Save" : "Create claim"}</button>
        </div>
      </div>
    </div>
  );
}

export function RejectModal({ app, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div data-testid="reject-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-bold">Reject listing</h3>
        <p className="text-slate-500 text-sm mt-1">{app.year} {app.make} {app.model} — {app.full_name}. The host will be emailed your reason.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" rows={4} className="belo-input mt-4" placeholder="Reason for rejection (e.g. vehicle too old, photos needed, mileage too high)…" />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 border-2 border-slate-200 text-slate-600 py-2.5 rounded-full font-semibold text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={() => onConfirm(reason)} data-testid="confirm-reject-btn" className="flex-1 bg-red-600 text-white py-2.5 rounded-full font-semibold text-sm hover:bg-red-700">Reject listing</button>
        </div>
      </div>
    </div>
  );
}
